<template>
  <section class="connect-how" :class="{ 'is-project': isProject }">
    <header class="connect-how--hero">
      <div class="hero--inner">
        <div class="hero--label">{{ badge }}</div>
        <h1>{{ title }}</h1>
        <p>{{ description }}</p>
      </div>
    </header>

    <section class="connect-how--section">
      <div class="section--header">
        <span>{{ badge }}</span>
        <h2>{{ workflowTitle }}</h2>
      </div>

      <div class="workflow--flow">
        <article v-for="(step, index) in steps" :key="step.title" class="workflow--card">
          <div class="workflow--number">{{ String(index + 1).padStart(2, '0') }}</div>
          <div class="workflow--content">
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
          <div
            v-if="index < steps.length - 1"
            class="workflow--arrow"
            :class="workflowArrowClass(index)"
          >
            <v-icon>mdi-arrow-right</v-icon>
          </div>
        </article>
      </div>
    </section>

    <section class="connect-how--section faq--section">
      <div class="section--header">
        <span>FAQ</span>
        <h2>Frequently Asked Questions</h2>
        <p>{{ faqDescription }}</p>
      </div>

      <div class="faq--list">
        <article
          v-for="(faq, index) in faqs"
          :key="faq.question"
          class="faq--item"
          :class="{ active: activeFaq === index }"
        >
          <button type="button" class="faq--question" @click="toggleFaq(index)">
            <span>{{ index + 1 }}. {{ faq.question }}</span>
            <v-icon class="faq--icon">mdi-chevron-down</v-icon>
          </button>
          <div class="faq--answer-shell">
            <div class="faq--answer">
              {{ faq.answer }}
            </div>
          </div>
        </article>
      </div>
    </section>

    <section v-if="!isMobileWebView" class="connect-how--cta">
      <div>
        <span>{{ badge }}</span>
        <h2>{{ ctaTitle }}</h2>
      </div>
      <div class="cta--actions">
        <button
          type="button"
          class="btn primary--action"
          @click="$router.push({ name: backRoute })"
        >
          <v-icon>mdi-arrow-left</v-icon>
          Kembali ke {{ backLabel }}
        </button>
      </div>
    </section>

    <section v-if="!isMobileWebView" class="connect-how--bottom-hero">
      <div class="bottom-hero--content">
        <span>{{ alternateBadge }}</span>
        <h2>{{ alternateHeroTitle }}</h2>
        <p>{{ alternateHeroDescription }}</p>
      </div>
      <router-link class="btn bottom-hero--action" :to="{ name: alternateHowRoute }">
        <span>{{ alternateHowLabel }}</span>
        <v-icon>mdi-arrow-right</v-icon>
      </router-link>
    </section>
  </section>
</template>

<script>
export default {
  name: 'connect-how-it-works',
  props: {
    type: {
      default: 'owner',
    },
  },
  data() {
    return {
      activeFaq: 0,
    };
  },
  computed: {
    isProject() {
      return this.type === 'project';
    },
    isMobileWebView() {
      return this.$route.query.mobile === 'true';
    },
    badge() {
      return this.isProject ? 'Project Connect' : 'Owner Connect';
    },
    title() {
      return this.isProject ? 'Project Connect' : 'Owner Connect';
    },
    description() {
      return this.isProject
        ? 'Dapatkan akses ke project primary aktif, shortlist peluang terbaik, lalu hubungi PIC resmi developer untuk mulai follow-up.'
        : 'Kelola inquiry owner yang siap dihubungi, shortlist peluang terbaik, lalu hubungi pemilik properti dengan alur yang rapi.';
    },
    workflowTitle() {
      return this.isProject ? 'Cara Kerja Project Connect' : 'Cara Kerja Owner Connect';
    },
    steps() {
      return this.isProject
        ? [
            {
              title: 'Pilih project primary',
              description: 'Filter lokasi, tipe properti, dan harga.',
            },
            {
              title: 'Pilih untuk lihat kontak PIC',
              description: 'Buka akses kontak PIC resmi project.',
            },
            {
              title: 'Masuk Favorit Saya',
              description: 'Project otomatis tersimpan di favorit.',
            },
            {
              title: 'Hubungi via WhatsApp',
              description: 'Mulai percakapan langsung dengan PIC.',
            },
            {
              title: 'Gunakan template pesan',
              description: 'Pakai pesan pembuka yang sudah disiapkan.',
            },
            {
              title: 'Konfirmasi detail project',
              description: 'Cek harga, promo, unit, komisi, dan ketentuan.',
            },
            {
              title: 'Koordinasikan survei',
              description: 'Atur izin dan jadwal survei bila perlu.',
            },
            {
              title: 'Pasarkan di BeliRumah.co',
              description: 'Gunakan materi dan detail yang sudah valid.',
            },
          ]
        : [
            {
              title: 'Pilih listing owner',
              description: 'Filter lokasi, tipe properti, dan harga.',
            },
            {
              title: 'Pilih untuk lihat kontak owner',
              description: 'Buka kontak owner yang ingin dibantu.',
            },
            {
              title: 'Masuk Favorit Saya',
              description: 'Listing otomatis tersimpan di favorit.',
            },
            {
              title: 'Hubungi via WhatsApp',
              description: 'Mulai percakapan langsung dengan owner.',
            },
            {
              title: 'Gunakan template pesan',
              description: 'Pakai pesan pembuka yang sudah disiapkan.',
            },
            {
              title: 'Konfirmasi detail listing',
              description: 'Cek lokasi, harga, status properti, dan komisi.',
            },
            {
              title: 'Koordinasikan survei',
              description: 'Atur jadwal untuk data dan foto properti.',
            },
            {
              title: 'Pasarkan di BeliRumah.co',
              description: 'Pasarkan setelah data properti valid.',
            },
          ];
    },
    faqs() {
      return this.isProject
        ? [
            {
              question: 'Apa itu Project Connect?',
              answer:
                'Project Connect merupakan fitur penghubung antara PIC Project Primary dengan Agen Properti aktif di aplikasi Agen BeliRumah yang memiliki paket membership Pro dan Executive.',
            },
            {
              question: 'Apakah yang saya hubungi adalah PIC Project tersebut?',
              answer:
                'Ya. Data kontak yang kami sediakan adalah PIC resmi dari Project Primary tersebut.',
            },
            {
              question:
                'Berapa komisi yang akan saya terima dari Project yang saya pasarkan dan berhasil jual?',
              answer:
                'Besaran komisi mengikuti ketentuan yang berlaku pada masing-masing proyek dan dapat dilihat pada detail listing project. Namun, kami menyarankan agen untuk melakukan konfirmasi kembali kepada PIC project karena besaran komisi atau program yang berlaku dapat berubah sewaktu-waktu mengikuti kebijakan developer.',
            },
            {
              question: 'Apakah saya perlu membagi komisi yang saya terima dengan BeliRumah.co?',
              answer:
                'Tidak. BeliRumah tidak mengambil bagian atau potongan komisi dari hasil penjualan yang diperoleh agen melalui fitur Project Connect. Komisi yang diterima sepenuhnya menjadi hak agen sesuai ketentuan yang berlaku pada proyek terkait.',
            },
          ]
        : [
            {
              question: 'Apa itu Owner Connect?',
              answer:
                'Owner Connect merupakan fitur penghubung antara pemilik properti yang sudah kami verifikasi dengan Agen Properti aktif di aplikasi Agen BeliRumah dengan paket membership Pro dan Executive. Melalui fitur ini, agen dapat langsung menghubungi pemilik properti melalui WhatsApp untuk menawarkan kerja sama pemasaran properti.',
            },
            {
              question: 'Apakah yang saya hubungi adalah owner properti langsung?',
              answer:
                'Ya, Data owner yang tersedia di Owner Connect telah melalui proses verifikasi oleh tim BeliRumah untuk memastikan bahwa kontak yang ditampilkan merupakan pemilik property.',
            },
            {
              question:
                'Berapa komisi yang akan saya terima jika memasarkan properti owner dan berhasil menjualnya?',
              answer:
                'Besaran komisi ditentukan berdasarkan kesepakatan antara agen dan pemilik properti. Secara umum, komisi yang ditawarkan berkisar antara 2%-3%, namun dapat berbeda pada setiap listing. Untuk informasi lebih detail, silakan menghubungi pemilik properti secara langsung untuk mengonfirmasi nilai komisi.',
            },
            {
              question: 'Apakah saya perlu membagi komisi yang saya terima dengan BeliRumah.co?',
              answer:
                'Tidak. BeliRumah tidak mengambil bagian atau potongan komisi dari hasil penjualan yang diperoleh agen melalui fitur Owner Connect. Komisi yang diterima sepenuhnya menjadi hak agen sesuai kesepakatan dengan pemilik properti.',
            },
          ];
    },
    faqDescription() {
      return this.isProject
        ? 'Jawaban singkat seputar akses PIC, komisi, dan aturan penggunaan Project Connect.'
        : 'Jawaban singkat seputar verifikasi owner, komisi, dan aturan penggunaan Owner Connect.';
    },
    ctaTitle() {
      return this.isProject
        ? 'Pilih project yang ingin Anda pasarkan melalui Project Connect.'
        : 'Shortlist owner yang ingin Anda bantu pasarkan.';
    },
    backRoute() {
      return this.isProject ? 'project-connect' : 'owner-connect';
    },
    backLabel() {
      return this.isProject ? 'Project Connect' : 'Owner Connect';
    },
    alternateHowRoute() {
      return this.isProject ? 'owner-connect-cara-kerja' : 'project-connect-cara-kerja';
    },
    alternateHowLabel() {
      return this.isProject ? 'Lihat cara kerja Owner Connect' : 'Lihat cara kerja Project Connect';
    },
    alternateBadge() {
      return this.isProject ? 'Owner Connect' : 'Project Connect';
    },
    alternateHeroTitle() {
      return this.isProject
        ? 'Butuh listing langsung dari pemilik properti?'
        : 'Butuh project primary siap dipasarkan?';
    },
    alternateHeroDescription() {
      return this.isProject
        ? 'Pelajari alur Owner Connect untuk menemukan owner yang siap dihubungi dan masuk ke pipeline listing Anda.'
        : 'Pelajari alur Project Connect untuk membuka akses PIC resmi project primary dan peluang komisi developer.';
    },
  },
  methods: {
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    },
    workflowArrowClass(index) {
      return {
        'workflow--arrow-down': index === 3,
        'workflow--arrow-left': index >= 4,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/utils/variables';

.connect-how {
  padding: 0 0 72px;
  color: #1f1f1f;
  font-family: Poppins, sans-serif;
}

.connect-how--hero {
  position: relative;
  min-height: 430px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 54px;
  border-radius: 0 0 22px 22px;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.3), rgba(18, 18, 18, 0.7)),
    url('~@/assets/img/building2.jpg');
  background-size: cover;
  background-position: center;
  //background: $color-beliruma-1;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0;
    height: 5px;
    background: $color-beliruma-1;
  }
}

.hero--inner {
  position: relative;
  z-index: 1;
  max-width: 860px;
  padding: 0 24px;
  text-align: center;
  color: #ffffff;

  h1 {
    margin: 14px 0 10px;
    font-family: Poppins-Bold, sans-serif;
    font-size: 56px;
    line-height: 1.12;
  }

  p {
    max-width: 760px;
    margin: 0 auto;
    color: rgba(255, 255, 255, 0.88);
    font-size: 17px;
    line-height: 1.7;
  }
}

.hero--label,
.section--header span,
.connect-how--cta span {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 13px;
  border-radius: 999px;
  font-family: Poppins-Bold, sans-serif;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0;
}

.hero--label {
  background: rgba(255, 255, 255, 0.94);
  color: $color-beliruma-1;
}

.cta--actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
}

.secondary--action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 17px;
  border: 1px solid #f0d1d3;
  border-radius: 999px;
  background: #ffffff;
  color: $color-beliruma-1;
  font-family: Poppins-Bold, sans-serif;
  font-size: 12px;
  text-decoration: none;
  transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

  .v-icon {
    color: $color-beliruma-1;
    font-size: 17px;
  }

  &:hover,
  &:focus {
    border-color: #e8b9bc;
    background: #fff1f1;
    color: $color-beliruma-1;
    text-decoration: none;
    transform: translateY(-1px);
  }
}

.connect-how--section {
  max-width: 1040px;
  margin: 0 auto 58px;
}

.section--header {
  max-width: 660px;
  margin: 0 auto 28px;
  text-align: center;

  span {
    background: #fff1f1;
    color: $color-beliruma-1;
  }

  h2 {
    margin: 14px 0 8px;
    color: #171717;
    font-family: Poppins-Bold, sans-serif;
    font-size: 32px;
    line-height: 1.2;
  }

  p {
    margin: 0;
    color: #666666;
    font-size: 14px;
    line-height: 1.7;
  }
}

.workflow--flow {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 28px 30px;
}

.workflow--card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 168px;
  padding: 18px 18px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(24, 24, 24, 0.045);
  isolation: isolate;
  animation: workflow-rise 0.46s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;

  &::before {
    content: none;
  }

  &:nth-child(2) {
    animation-delay: 0.04s;
  }

  &:nth-child(3) {
    animation-delay: 0.08s;
  }

  &:nth-child(4) {
    animation-delay: 0.12s;
  }

  &:nth-child(5) {
    animation-delay: 0.16s;
  }

  &:nth-child(6) {
    animation-delay: 0.2s;
  }

  &:nth-child(7) {
    animation-delay: 0.24s;
  }

  &:nth-child(8) {
    animation-delay: 0.28s;
  }

  @media (min-width: 1366px) {
    &:nth-child(5) {
      grid-column: 4;
      grid-row: 2;
    }

    &:nth-child(6) {
      grid-column: 3;
      grid-row: 2;
    }

    &:nth-child(7) {
      grid-column: 2;
      grid-row: 2;
    }

    &:nth-child(8) {
      grid-column: 1;
      grid-row: 2;
    }
  }

  &:hover {
    border-color: #dddddd;
    box-shadow: 0 16px 30px rgba(24, 24, 24, 0.07);
    transform: translateY(-3px);
  }

  h3 {
    margin: 0 0 7px;
    color: #1b1b1b;
    font-family: Poppins-Bold, sans-serif;
    font-size: 15px;
    line-height: 1.35;
  }

  p {
    margin: 0;
    color: #626262;
    font-size: 12px;
    line-height: 1.55;
  }
}

.workflow--number {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-bottom: 18px;
  border-radius: 50%;
  background: #fff5f5;
  border: 1px solid #f3c9cc;
  color: $color-beliruma-1;
  font-family: Poppins-Bold, sans-serif;
  font-size: 14px;
  box-shadow: none;
  transition: background-color 0.22s ease, border-color 0.22s ease, transform 0.22s ease;
}

.workflow--card:hover .workflow--number {
  background: #ffffff;
  border-color: $color-beliruma-1;
  transform: scale(1.04);
}

.workflow--content {
  position: relative;
  z-index: 1;
}

.workflow--arrow {
  position: absolute;
  top: 50%;
  right: -16px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(215, 25, 32, 0.18);
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 10px 22px rgba(19, 24, 31, 0.08);
  transform: translateY(-50%);

  .v-icon {
    color: $color-beliruma-1;
    font-size: 18px;
  }

  &.workflow--arrow-down {
    top: auto;
    right: 50%;
    bottom: -16px;
    transform: translateX(50%) rotate(90deg);
  }

  &.workflow--arrow-left {
    right: auto;
    left: -16px;
    transform: translateY(-50%) rotate(180deg);
  }

  @media #{$tablets} {
    top: auto;
    right: 50%;
    bottom: -15px;
    left: auto;
    width: 28px;
    height: 28px;
    transform: translateX(50%) rotate(90deg);

    &.workflow--arrow-down,
    &.workflow--arrow-left {
      top: auto;
      right: 50%;
      bottom: -15px;
      left: auto;
      transform: translateX(50%) rotate(90deg);
    }
  }

  @media #{$phones} {
    top: auto;
    right: 50%;
    bottom: -15px;
    width: 28px;
    height: 28px;
    transform: translateX(50%) rotate(90deg);

    &.workflow--arrow-down,
    &.workflow--arrow-left {
      top: auto;
      right: 50%;
      bottom: -15px;
      left: auto;
      transform: translateX(50%) rotate(90deg);
    }
  }
}

.faq--section {
  max-width: 920px;
}

.faq--list {
  display: grid;
  gap: 10px;
}

.faq--item {
  border: 1px solid #eeeeee;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(24, 24, 24, 0.035);
  overflow: hidden;
  animation: faq-rise 0.38s ease both;
  transform: translateZ(0);
  transition: border-color 0.28s ease, box-shadow 0.28s ease, transform 0.28s ease;

  &.active {
    border-color: #f0d1d3;
    box-shadow: 0 14px 30px rgba(213, 18, 27, 0.08);
    transform: translateY(-2px);
  }

  &:nth-child(2) {
    animation-delay: 0.05s;
  }

  &:nth-child(3) {
    animation-delay: 0.1s;
  }

  &:nth-child(4) {
    animation-delay: 0.15s;
  }
}

.faq--question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 58px;
  padding: 16px 18px;
  border: 0;
  background: #ffffff;
  color: #252525;
  font-family: Poppins-Bold, sans-serif;
  font-size: 14px;
  line-height: 1.45;
  text-align: left;
  transition: background-color 0.28s ease, color 0.28s ease;

  .v-icon {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: $color-beliruma-1;
    color: #ffffff;
    font-size: 17px;
    transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.28s ease;
  }
}

.faq--item.active .faq--question {
  background: #fffafa;
}

.faq--item.active .faq--icon {
  transform: rotate(180deg);
}

.faq--answer-shell {
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-4px);
  transition: grid-template-rows 0.38s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease,
    transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: grid-template-rows, opacity, transform;
}

.faq--item.active .faq--answer-shell {
  grid-template-rows: 1fr;
  opacity: 1;
  transform: translateY(0);
}

.faq--answer {
  min-height: 0;
  overflow: hidden;
  padding: 0 18px;
  color: #626262;
  font-size: 13px;
  line-height: 1.75;
  transition: padding-bottom 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.faq--item.active .faq--answer {
  padding-bottom: 18px;
}

@keyframes faq-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes workflow-rise {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .workflow--card,
  .workflow--number,
  .faq--item,
  .faq--question,
  .faq--question .v-icon,
  .faq--answer-shell {
    animation: none;
    transition: none;
  }

  .workflow--card:hover,
  .workflow--card:hover .workflow--number {
    transform: none;
  }
}

.connect-how--cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  max-width: 1040px;
  margin: 0 auto 24px;
  padding: 24px;
  border: 1px solid #f0d1d3;
  border-radius: 16px;
  background: linear-gradient(135deg, #fff7f7 0%, #ffffff 68%);

  span {
    background: #ffffff;
    color: $color-beliruma-1;
  }

  h2 {
    margin: 12px 0 0;
    color: #171717;
    font-family: Poppins-Bold, sans-serif;
    font-size: 23px;
    line-height: 1.3;
  }
}

.connect-how--bottom-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  max-width: 1040px;
  min-height: 210px;
  margin: 0 auto;
  padding: 30px;
  border-radius: 18px;
  background-image: linear-gradient(90deg, rgba(18, 18, 18, 0.78), rgba(18, 18, 18, 0.34)),
    url('~@/assets/img/gedung.jpg');
  background-size: cover;
  background-position: center;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0;
    height: 4px;
    background: $color-beliruma-1;
  }
}

.bottom-hero--content {
  position: relative;
  z-index: 1;
  max-width: 620px;
  color: #ffffff;

  span {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 0 13px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.94);
    color: $color-beliruma-1;
    font-family: Poppins-Bold, sans-serif;
    font-size: 11px;
  }

  h2 {
    margin: 14px 0 8px;
    font-family: Poppins-Bold, sans-serif;
    font-size: 28px;
    line-height: 1.25;
  }

  p {
    max-width: 560px;
    margin: 0;
    color: rgba(255, 255, 255, 0.86);
    font-size: 14px;
    line-height: 1.7;
  }
}

.bottom-hero--action {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  background: $color-beliruma-1;
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  font-size: 13px;
  text-decoration: none;

  .v-icon {
    color: #ffffff;
    font-size: 18px;
  }

  &:hover,
  &:focus {
    color: #ffffff;
    text-decoration: none;
  }
}

.primary--action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  background: $color-beliruma-1;
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  font-size: 13px;

  .v-icon {
    color: #ffffff;
    font-size: 18px;
  }
}

@media #{$tablets} {
  .connect-how--hero {
    min-height: 360px;
  }

  .hero--inner h1 {
    font-size: 46px;
  }

  .connect-how--section,
  .connect-how--cta,
  .connect-how--bottom-hero {
    max-width: 100%;
  }

  .workflow--flow {
    grid-template-columns: 1fr;
    gap: 14px;
    padding-left: 18px;

    &::before {
      content: '';
      position: absolute;
      top: 22px;
      bottom: 22px;
      left: 21px;
      width: 2px;
      background: linear-gradient(180deg, rgba(213, 18, 27, 0.18), rgba(210, 210, 210, 0.7));
    }
  }

  .workflow--card {
    min-height: 0;
    padding: 16px 16px 16px 20px;
  }

  .workflow--number {
    width: 38px;
    height: 38px;
    margin-bottom: 12px;
  }
}

@media #{$phones} {
  .connect-how {
    padding-bottom: 48px;
  }

  .connect-how--hero {
    min-height: 310px;
    margin-bottom: 38px;
    border-radius: 0 0 18px 18px;
  }

  .hero--inner {
    h1 {
      font-size: 38px;
    }

    p {
      font-size: 14px;
    }
  }

  .connect-how--section {
    margin-bottom: 42px;
  }

  .section--header {
    margin-bottom: 20px;

    h2 {
      font-size: 25px;
    }
  }

  .workflow--flow {
    grid-template-columns: 1fr;
    gap: 14px;
    padding-left: 18px;

    &::before {
      content: '';
      position: absolute;
      top: 22px;
      bottom: 22px;
      left: 21px;
      width: 2px;
      background: linear-gradient(180deg, rgba(213, 18, 27, 0.18), rgba(210, 210, 210, 0.7));
    }
  }

  .workflow--card {
    min-height: 0;
    padding: 16px 16px 16px 20px;
  }

  .workflow--number {
    width: 38px;
    height: 38px;
    margin-bottom: 12px;
  }

  .faq--question {
    align-items: flex-start;
    padding: 15px;
  }

  .faq--answer {
    padding: 0 15px 16px;
  }

  .connect-how--cta {
    display: block;
    padding: 20px;

    h2 {
      margin-bottom: 18px;
      font-size: 20px;
    }
  }

  .primary--action {
    width: 100%;
  }

  .cta--actions {
    display: grid;
    gap: 10px;
  }

  .secondary--action {
    width: 100%;
  }

  .connect-how--bottom-hero {
    display: block;
    min-height: 0;
    padding: 22px;
  }

  .bottom-hero--content {
    h2 {
      font-size: 22px;
    }
  }

  .bottom-hero--action {
    width: 100%;
    margin-top: 18px;
  }
}
</style>
